import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import {
  LayoutDashboard,
  Shield,
  BarChart3,
  FileSearch,
  Lock,
  ChevronRight,
  Users,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';

// Dashboard configurations
const DASHBOARDS = [
  {
    id: 'executive',
    name: 'Executive Hub',
    description: 'Strategic overview for C-suite executives',
    icon: LayoutDashboard,
    color: 'emerald',
    perspective: 'Top-Down',
    allowedRoles: ['cso', 'ceo', 'admin'],
    metrics: ['Compliance Score', 'Risk Heat Map', 'Board KPIs', 'Deadlines'],
    path: '/dashboard/executive'
  },
  {
    id: 'compliance',
    name: 'Compliance Console',
    description: 'Regulatory tracking and gap analysis',
    icon: Shield,
    color: 'blue',
    perspective: 'Middle-Out',
    allowedRoles: ['cso', 'compliance_manager', 'admin'],
    metrics: ['Gap Analysis', 'KPI Mapping', 'Framework Status', 'Remediation'],
    path: '/dashboard/compliance'
  },
  {
    id: 'analyst',
    name: 'Data Lab',
    description: 'Granular data analysis and methodology',
    icon: BarChart3,
    color: 'purple',
    perspective: 'Bottom-Up',
    allowedRoles: ['cso', 'compliance_manager', 'analyst', 'admin'],
    metrics: ['Data Pipeline', 'Emissions Calc', 'Quality Scores', 'Methodology'],
    path: '/dashboard/analyst'
  },
  {
    id: 'auditor',
    name: 'Audit Vault',
    description: 'Audit trails and integrity verification',
    icon: FileSearch,
    color: 'amber',
    perspective: 'Cross-Cutting',
    allowedRoles: ['cso', 'auditor', 'admin'],
    metrics: ['Audit Trail', 'Data Lineage', 'Integrity Proofs', 'Access Logs'],
    path: '/dashboard/auditor'
  }
];

// Role hierarchy
const ROLE_HIERARCHY = {
  'admin': ['executive', 'compliance', 'analyst', 'auditor'],
  'cso': ['executive', 'compliance', 'analyst', 'auditor'],
  'ceo': ['executive'],
  'compliance_manager': ['compliance', 'analyst'],
  'analyst': ['analyst'],
  'auditor': ['auditor', 'compliance', 'analyst'] // Read-only for compliance/analyst
};

const DashboardCard = ({ dashboard, hasAccess, onSelect, stats }) => {
  const Icon = dashboard.icon;
  const colorClasses = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:border-emerald-400',
    blue: 'bg-blue-50 text-blue-700 border-blue-200 hover:border-blue-400',
    purple: 'bg-purple-50 text-purple-700 border-purple-200 hover:border-purple-400',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 hover:border-amber-400'
  };
  const iconColorClasses = {
    emerald: 'bg-emerald-100 text-emerald-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    amber: 'bg-amber-100 text-amber-600'
  };

  return (
    <button
      onClick={() => hasAccess && onSelect(dashboard)}
      disabled={!hasAccess}
      data-testid={`dashboard-card-${dashboard.id}`}
      className={`relative w-full text-left p-6 rounded-xl border-2 transition-all duration-200 ${
        hasAccess 
          ? `${colorClasses[dashboard.color]} cursor-pointer hover:shadow-lg` 
          : 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
      }`}
    >
      {!hasAccess && (
        <div className="absolute top-4 right-4">
          <Lock size={20} className="text-slate-400" />
        </div>
      )}
      
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
          hasAccess ? iconColorClasses[dashboard.color] : 'bg-slate-100 text-slate-400'
        }`}>
          <Icon size={24} />
        </div>
        
        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-1">{dashboard.name}</h3>
          <p className={`text-sm mb-3 ${hasAccess ? 'opacity-80' : 'text-slate-400'}`}>
            {dashboard.description}
          </p>
          
          <div className="flex items-center gap-2 text-xs mb-3">
            <span className={`px-2 py-1 rounded-full ${
              hasAccess ? 'bg-white/50' : 'bg-slate-100'
            }`}>
              {dashboard.perspective}
            </span>
          </div>
          
          {hasAccess && stats && (
            <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-current/10">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-xs">
                  <span className="opacity-70">{stat.label}:</span>
                  <span className="ml-1 font-semibold">{stat.value}</span>
                </div>
              ))}
            </div>
          )}
          
          {hasAccess && (
            <div className="flex items-center gap-1 mt-4 text-sm font-medium">
              Enter Dashboard <ChevronRight size={16} />
            </div>
          )}
          
          {!hasAccess && (
            <div className="mt-4 text-sm text-slate-500">
              Contact admin for access
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const DashboardSelector = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState('cso'); // Default to CSO for demo
  const [dashboardStats, setDashboardStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [recentAccess, setRecentAccess] = useState([]);

  const fetchDashboardStats = async () => {
    // Fetch stats for each dashboard preview
    const [summaryRes, complianceRes] = await Promise.all([
      apiFetch(`${API}/dashboard/summary`),
      apiFetch(`${API}/compliance/quick-status`)
    ]);
    const summaryData = summaryRes?._error ? {} : summaryRes;
    const complianceData = complianceRes?._error ? {} : complianceRes;

    setDashboardStats({
      executive: [
        { label: 'Compliance', value: `${complianceData?.quick_score || 0}%` },
        { label: 'Risks', value: summaryData?.risks?.critical || 0 },
        { label: 'Frameworks', value: summaryData?.frameworks?.length || 0 },
        { label: 'Indicators', value: summaryData?.indicators?.total || 0 }
      ],
      compliance: [
        { label: 'Gap Score', value: `${100 - (complianceData?.quick_score || 0)}%` },
        { label: 'Uploads', value: complianceData?.total_uploads || 0 },
        { label: 'Frameworks', value: '4 Active' },
        { label: 'Status', value: complianceData?.status || 'N/A' }
      ],
      analyst: [
        { label: 'Data Sources', value: summaryData?.datasources?.total || 0 },
        { label: 'Indicators', value: summaryData?.indicators?.total || 0 },
        { label: 'Quality', value: '94%' },
        { label: 'Pipelines', value: '3 Active' }
      ],
      auditor: [
        { label: 'Audit Entries', value: '24' },
        { label: 'Integrity', value: '100%' },
        { label: 'Lineage', value: 'Complete' },
        { label: 'Last Check', value: 'Today' }
      ]
    });

    setLoading(false);
  };

  const loadRecentAccess = () => {
    const recent = JSON.parse(localStorage.getItem('recentDashboardAccess') || '[]');
    setRecentAccess(recent.slice(0, 3));
  };

   
  useEffect(() => {
    fetchDashboardStats();
    loadRecentAccess();
  }, []);

  const logDashboardAccess = async (dashboard) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // Log to backend audit trail
      const logRes = await apiFetch(`${API}/audit/log-access`, {
        method: 'POST',
        body: {
          dashboard_id: dashboard.id,
          dashboard_name: dashboard.name,
          user_email: user.email,
          user_role: userRole,
          timestamp: new Date().toISOString(),
          action: 'dashboard_access'
        }
      });
      if (logRes?._error) {
        // Silently fail if endpoint doesn't exist yet
        console.log('Audit logging endpoint not available');
      }

      // Store in local storage for recent access
      const recent = JSON.parse(localStorage.getItem('recentDashboardAccess') || '[]');
      const newEntry = {
        id: dashboard.id,
        name: dashboard.name,
        timestamp: new Date().toISOString()
      };
      const updated = [newEntry, ...recent.filter(r => r.id !== dashboard.id)].slice(0, 5);
      localStorage.setItem('recentDashboardAccess', JSON.stringify(updated));

    } catch (error) {
      console.error('Error logging dashboard access:', error);
    }
  };

  const handleDashboardSelect = (dashboard) => {
    logDashboardAccess(dashboard);
    navigate(dashboard.path);
  };

  const hasAccess = (dashboard) => {
    const allowedDashboards = ROLE_HIERARCHY[userRole] || [];
    return allowedDashboards.includes(dashboard.id);
  };

  const accessibleCount = DASHBOARDS.filter(d => hasAccess(d)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-emerald-700 flex items-center justify-center animate-pulse">
            <LayoutDashboard size={24} className="text-white" />
          </div>
          <p className="text-sm text-slate-500">Loading dashboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8" data-testid="dashboard-selector">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard Selector
          </h1>
          <p className="text-lg text-slate-600">
            Choose your workspace based on your role and responsibilities
          </p>
        </div>
        
        {/* Role Indicator */}
        <div className="flex items-center gap-3 px-4 py-2 bg-slate-100 rounded-lg">
          <Users size={18} className="text-slate-500" />
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Current Role</p>
            <select
              value={userRole}
              onChange={(e) => setUserRole(e.target.value)}
              data-testid="role-selector"
              className="text-sm font-medium text-slate-900 bg-transparent border-none cursor-pointer focus:outline-none"
            >
              <option value="cso">Chief Sustainability Officer</option>
              <option value="compliance_manager">Compliance Manager</option>
              <option value="analyst">Data Analyst</option>
              <option value="auditor">Internal Auditor</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
        </div>
      </div>

      {/* Access Summary */}
      <div className="flex items-center gap-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
        <CheckCircle className="text-emerald-600" size={24} />
        <div>
          <p className="text-sm font-medium text-emerald-900">
            You have access to {accessibleCount} of {DASHBOARDS.length} dashboards
          </p>
          <p className="text-xs text-emerald-700">
            Based on your role: {userRole.replace('_', ' ').toUpperCase()}
          </p>
        </div>
      </div>

      {/* Recent Access */}
      {recentAccess.length > 0 && (
        <div className="p-4 bg-slate-50 rounded-lg">
          <h3 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
            <Clock size={16} /> Recent Activity
          </h3>
          <div className="flex gap-3">
            {recentAccess.map((access, idx) => (
              <button
                key={idx}
                onClick={() => {
                  const dashboard = DASHBOARDS.find(d => d.id === access.id);
                  if (dashboard && hasAccess(dashboard)) {
                    handleDashboardSelect(dashboard);
                  }
                }}
                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm hover:border-emerald-300 transition-colors"
              >
                {access.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Dashboard Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {DASHBOARDS.map((dashboard) => (
          <DashboardCard
            key={dashboard.id}
            dashboard={dashboard}
            hasAccess={hasAccess(dashboard)}
            onSelect={handleDashboardSelect}
            stats={dashboardStats[dashboard.id]}
          />
        ))}
      </div>

      {/* Access Legend */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <h3 className="text-sm font-medium text-slate-700 mb-3">Access Hierarchy</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span>CSO: All Dashboards</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span>Compliance: Console + Lab</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-purple-500" />
            <span>Analyst: Data Lab Only</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span>Auditor: Vault + Read-Only</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSelector;
